<template>
  <div>
    <h2 class="title is-2">
      Licensed items
    </h2>

    <b-notification
      v-if="licensesLoaded && showCheckoutSuccess"
      type="is-success"
      :closable="true"
    >
      Thank you! Your track{{ tracksPurchased > 1 ? 's are' : ' is' }} available for download below.
    </b-notification>

    <div class="tabs">
      <ul>
        <li
          class="is-active"
          @click="openTab('tab-licenses-tracks', $event)"
        >
          <a>Tracks</a>
        </li>
        <li
          @click="openTab('tab-licenses-sfx', $event)"
        >
          <a>Sfx</a>
        </li>
      </ul>
    </div>

    <div
      id="tab-licenses-tracks"
      class="tab-content"
    >
      <template
        v-if="licensesLoaded && licenses.length > 0"
      >
        <track-list-item
          v-for="license in licenses"
          :key="license.pk"
          :track="license.track"
          :show-waveform="false"
        >
          <div class="column is-narrow">
            {{ license.license_type.name }}
            <b-tooltip
              v-if="license.order_item.unit_amount > 0"
              label="This is a single-use license and may only be used in one project."
              type="is-dark"
              position="is-top"
            >
              &nbsp;<span class="tag small is-rounded is-info">Single use</span>
            </b-tooltip>
          </div>
          <div class="column is-narrow">
            {{ license.created_at|formattedDate }}
          </div>
          <div class="column is-narrow">
            <div class="buttons are-small">
              <a
                class="button"
                @click="handlePdfDownload(license.pk)"
              >License</a>
              &nbsp;
              <a
                v-if="getWavUrl(license)"
                class="button"
                download
                :href="getWavUrl(license)"
              >WAV</a>
              &nbsp;
              <a
                v-if="getMp3Url(license)"
                class="button"
                download
                :href="getMp3Url(license)"
              >MP3</a>
            </div>
          </div>
        </track-list-item>
      </template>

      <b-notification
        v-if="licensesLoaded && licenses.length === 0"
        :closable="false"
      >
        You have no licensed tracks.
      </b-notification>

      <div
        v-if="!licensesLoaded"
        class="columns"
      >
        <div class="column is-12 has-text-centered">
          <br>
          <span
            class="loader xl"
            style="display: inline-block; width: 3em; height: 3em;"
          />
        </div>
      </div>
    </div>

    <div
      id="tab-licenses-sfx"
      class="tab-content"
      style="display: none;"
    >
      <b-notification
        v-if="sfxLoaded && sfxDownloadLogEntries.length === 0"
        :closable="false"
      >
        You have not yet downloaded any Sfx.
      </b-notification>

      <div
        v-if="!sfxLoaded"
        class="columns"
      >
        <div class="column is-12 has-text-centered">
          <br>
          <span
            class="loader xl"
            style="display: inline-block; width: 3em; height: 3em;"
          />
        </div>
      </div>

      <template
        v-if="sfxLoaded && sfxDownloadLogEntries.length > 0"
      >
        <table class="table is-fullwidth is-striped is-hoverable">
          <thead>
            <tr>
              <th>Sfx</th>
              <th>Library</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="download in sfxDownloadLogEntries"
              :key="download.id"
            >
              <td>{{ download.metadata.description }}</td>
              <td>{{ download.metadata.library }}</td>
              <td>{{ download.created_at|formattedDate }}</td>
            </tr>
          </tbody>
        </table>
      </template>
    </div>
  </div>
</template>

<script>
import licensesClient from '@/api/licenses.js'
import TrackListItem from '@/components/TrackListItem.vue'

export default {
  name: 'LicenseList',

  components: {
    TrackListItem
  },

  props: {
    showCheckoutSuccess: {
      type: Boolean,
      required: false,
      default: false
    },
    tracksPurchased: {
      type: Number,
      required: false,
      default: null
    }
  },

  data () {
    return {
      downloadUrlCache: {},
      licensesLoaded: false,
      sfxLoaded: false
    }
  },

  computed: {
    licenses: function () {
      return this.$store.getters['licenses/licenses']
    },
    sfxDownloadLogEntries: function () {
      return this.$store.getters['sfxDownloadLog/sfxDownloadLogEntries']
    },
    user: function () {
      return this.$store.getters['user/currentUser']
    },
    fullName: function () {
      return this.$store.getters['user/fullName']
    }
  },

  created () {
    this.$store
      .dispatch('licenses/fetchLicenses')
      .then(licenses => {
        this.licensesLoaded = true
        const tracks = licenses.map(license => license.track)
        if (tracks.length > 0) {
          this.$store.dispatch('player/loadTracks', tracks)
        }
      })
    this.$store.dispatch('sfxDownloadLog/fetchLogEntries')
      .then(() => {
        this.sfxLoaded = true
      })
  },

  methods: {
    handlePdfDownload (pk) {
      licensesClient.pdf(pk).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `license-${pk}.pdf`)
        document.body.appendChild(link)
        link.click()
      })
    },

    _getAudioUrl (license, fileType) {
      const key = `${license.pk}-${fileType}`
      if (!(key in this.downloadUrlCache)) {
        let value = false
        const validTokens = license.download_tokens.filter(
          token => token.is_expired === false
        )
        if (validTokens.length > 0) {
          value = validTokens[0][fileType]
        }
        this.downloadUrlCache[key] = value
      }
      return this.downloadUrlCache[key]
    },

    getWavUrl (license) {
      return this._getAudioUrl(license, 'wav')
    },

    getMp3Url (license) {
      return this._getAudioUrl(license, 'mp3')
    },

    openTab (tabId, event) {
      // Show/hide tab content
      const tabs = document.getElementsByClassName('tab-content')
      for (let i = 0; i < tabs.length; i++) {
        tabs[i].style.display = 'none'
      }
      document.getElementById(tabId).style.display = 'block'

      // Update active tab
      const tabHeaders = document.getElementsByClassName('tabs')[0].getElementsByTagName('li')
      for (let i = 0; i < tabHeaders.length; i++) {
        tabHeaders[i].classList.remove('is-active')
        if (tabHeaders[i].getElementsByTagName('a')[0] === event.target) {
          tabHeaders[i].classList.add('is-active')
        }
      }
    }
  },

  metaInfo: {
    title: 'Licensed items'
  }
}
</script>

<style>
</style>
